import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Loader2, Users, Plus, Trash2, Wallet, MapPin, Car, UserPlus,
  Building2, Save, X, Link2, Unlink, History, Download, ArrowDownLeft, ArrowUpRight,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Badge } from "../ui/badge";

function fmtXAF(n) { return `${Number(n || 0).toLocaleString("fr-FR").replace(/,/g, " ")} F`; }

export default function AdminPartnersPanel() {
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [rechargeFor, setRechargeFor] = useState(null); // partner obj
  const [linking, setLinking] = useState(null); // { type: 'driver'|'vehicle', partner }
  const [historyFor, setHistoryFor] = useState(null); // partner obj
  const [history, setHistory] = useState(null);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [allDrivers, setAllDrivers] = useState([]);
  const [allVehicles, setAllVehicles] = useState([]);
  const [zoneOptions, setZoneOptions] = useState([]);

  const load = () => {
    setLoading(true);
    api.get("/admin/partners")
      .then(({ data }) => setPartners(data.partners || []))
      .catch(() => toast.error("Impossible de charger les partenaires"))
      .finally(() => setLoading(false));
    api.get("/admin/express/config").then(({ data }) => setZoneOptions(data.zones || [])).catch(() => {});
    api.get("/admin/drivers/express").then(({ data }) => setAllDrivers(data.drivers || [])).catch(() => {});
    api.get("/vehicles").then(({ data }) => setAllVehicles(data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le partenaire "${p.partner_name}" ? Les chauffeurs / véhicules seront détachés.`)) return;
    try {
      await api.delete(`/admin/partners/${p.id}`);
      toast.success("Partenaire supprimé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const openHistory = async (partner) => {
    setHistoryFor(partner);
    setHistoryBusy(true); setHistory(null);
    try {
      const { data } = await api.get(`/admin/partners/${partner.id}/tft-history`);
      setHistory(data);
    } catch (e) { toast.error("Impossible de charger l'historique"); }
    finally { setHistoryBusy(false); }
  };

  const downloadCsv = async (endpoint, filename) => {
    try {
      const res = await api.get(endpoint, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Téléchargement échoué"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-partners-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 mr-auto">
          <Building2 className="w-5 h-5 text-[#F2A93B]" />
          <h2 className="text-lg font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>Partenaires / Flottes</h2>
        </div>
        <span className="text-xs text-[#5B6B7A]"><b className="text-[#0F2B46]">{partners.length}</b> partenaire(s)</span>
        <Button size="sm" onClick={() => setShowCreate(true)} className="bg-[#F2A93B] text-[#0F2B46] hover:bg-[#E09520] font-bold" data-testid="partner-create-btn">
          <Plus className="w-4 h-4 mr-1" /> Nouveau partenaire
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#0F2B46]" /></div>
      ) : partners.length === 0 ? (
        <div className="py-10 text-center text-sm text-[#5B6B7A] bg-white border border-[#E4E0D8] rounded-2xl">
          Aucun partenaire pour l&apos;instant. Créez un compte flotte pour lier plusieurs chauffeurs et véhicules à une même caisse TFT.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {partners.map((p) => (
            <div key={p.id} className="bg-white border border-[#E4E0D8] rounded-2xl p-4" data-testid={`partner-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <div className="text-base font-extrabold text-[#0F2B46] truncate" style={{ fontFamily: "Sora" }}>{p.partner_name}</div>
                  <div className="text-[11px] text-[#5B6B7A] truncate">{p.email}</div>
                  {p.phone && <div className="text-[11px] text-[#5B6B7A]">{p.phone}</div>}
                </div>
                <Button size="icon" variant="ghost" onClick={() => remove(p)} className="text-[#EF4444]" data-testid={`partner-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>

              {/* TFT balance */}
              <div className="p-3 rounded-xl bg-gradient-to-br from-[#0F2B46] to-[#173A5E] text-white mb-3">
                <div className="text-[10px] uppercase tracking-widest text-[#CFE0EF]">Solde TFT flotte</div>
                <div className="flex items-end justify-between mt-1">
                  <div className="text-xl font-extrabold text-[#F2A93B]" style={{ fontFamily: "Sora" }} data-testid={`partner-balance-${p.id}`}>{fmtXAF(p.tft_balance)}</div>
                  <Button size="sm" onClick={() => setRechargeFor(p)} className="bg-[#F2A93B] text-[#0F2B46] hover:bg-[#E09520] font-bold h-7" data-testid={`partner-recharge-${p.id}`}>
                    <Wallet className="w-3 h-3 mr-1" /> Recharger
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center mb-3">
                <div className="p-2 rounded-lg bg-[#F4F2ED]">
                  <div className="text-[10px] uppercase text-[#5B6B7A]">Chauffeurs</div>
                  <div className="text-base font-bold text-[#0F2B46]">{p.partner_drivers.length}</div>
                </div>
                <div className="p-2 rounded-lg bg-[#F4F2ED]">
                  <div className="text-[10px] uppercase text-[#5B6B7A]">Véhicules</div>
                  <div className="text-base font-bold text-[#0F2B46]">{p.partner_vehicles.length}</div>
                </div>
                <div className="p-2 rounded-lg bg-[#F4F2ED]">
                  <div className="text-[10px] uppercase text-[#5B6B7A]">Zones</div>
                  <div className="text-base font-bold text-[#0F2B46]">{p.partner_zones.length}</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-1 mb-3">
                {p.partner_zones.map((z) => (
                  <Badge key={z} variant="secondary" className="text-[10px]"><MapPin className="w-2.5 h-2.5 mr-1" />{z}</Badge>
                ))}
              </div>

              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => setLinking({ type: "driver", partner: p })} className="border-[#CBD5E1] flex-1 min-w-[100px]" data-testid={`partner-link-driver-${p.id}`}>
                  <UserPlus className="w-3.5 h-3.5 mr-1" /> Chauffeurs
                </Button>
                <Button size="sm" variant="outline" onClick={() => setLinking({ type: "vehicle", partner: p })} className="border-[#CBD5E1] flex-1 min-w-[100px]" data-testid={`partner-link-vehicle-${p.id}`}>
                  <Car className="w-3.5 h-3.5 mr-1" /> Véhicules
                </Button>
                <Button size="sm" variant="outline" onClick={() => openHistory(p)} className="border-[#CBD5E1]" data-testid={`partner-history-${p.id}`}>
                  <History className="w-3.5 h-3.5 mr-1" /> Historique
                </Button>
                <Button size="sm" variant="outline" onClick={() => setSelected(p)} className="border-[#CBD5E1]" data-testid={`partner-detail-${p.id}`}>
                  Détails
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create partner */}
      <CreatePartnerDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        zoneOptions={zoneOptions}
        onCreated={() => { setShowCreate(false); load(); }}
      />

      {/* Recharge */}
      <RechargeDialog
        partner={rechargeFor}
        onClose={() => setRechargeFor(null)}
        onRecharged={() => { setRechargeFor(null); load(); }}
      />

      {/* Link driver / vehicle */}
      <LinkDialog
        linking={linking}
        onClose={() => setLinking(null)}
        allDrivers={allDrivers}
        allVehicles={allVehicles}
        onChanged={load}
      />

      {/* Detail */}
      <PartnerDetailDialog
        partner={selected}
        onClose={() => setSelected(null)}
        zoneOptions={zoneOptions}
        onSaved={() => { setSelected(null); load(); }}
      />

      {/* TFT History */}
      <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="admin-partner-history-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F2B46] flex items-center gap-2" style={{ fontFamily: "Sora" }}>
              <History className="w-5 h-5 text-[#F2A93B]" /> Historique TFT — {historyFor?.partner_name}
            </DialogTitle>
          </DialogHeader>
          {historyBusy || !history ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#0F2B46]" /></div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="p-3 rounded-xl bg-[#0F2B46] text-white">
                  <div className="text-[10px] uppercase text-[#CFE0EF]">Solde actuel</div>
                  <div className="text-base font-extrabold text-[#F2A93B]">{fmtXAF(history.current_balance)}</div>
                </div>
                <div className="p-3 rounded-xl bg-emerald-50">
                  <div className="text-[10px] uppercase text-emerald-700 flex items-center gap-1"><ArrowDownLeft className="w-3 h-3" />Recharges reçues</div>
                  <div className="text-base font-extrabold text-emerald-700">+{fmtXAF(history.total_recharged_in)}</div>
                </div>
                <div className="p-3 rounded-xl bg-red-50">
                  <div className="text-[10px] uppercase text-red-700 flex items-center gap-1"><ArrowUpRight className="w-3 h-3" />Distribué</div>
                  <div className="text-base font-extrabold text-red-700">-{fmtXAF(history.total_recharged_out)}</div>
                </div>
              </div>
              <div className="mb-3 flex justify-end">
                <Button size="sm" onClick={() => downloadCsv(`/admin/partners/${historyFor.id}/tft-history.csv`, `tft_history_${historyFor.id}.csv`)} className="bg-[#F2A93B] text-[#0F2B46] hover:bg-[#E09520] font-bold" data-testid="admin-history-download-csv">
                  <Download className="w-3.5 h-3.5 mr-1" /> Télécharger CSV
                </Button>
              </div>
              {history.transactions.length === 0 ? (
                <div className="py-8 text-center text-sm text-[#5B6B7A]">Aucun mouvement TFT.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px]">
                    <thead>
                      <tr className="text-left text-[10.5px] uppercase font-semibold text-[#5B6B7A] border-b border-[#E4E0D8]">
                        <th className="py-1.5 pr-2">Date</th>
                        <th className="py-1.5 pr-2">Type</th>
                        <th className="py-1.5 pr-2">Chauffeur</th>
                        <th className="py-1.5 pr-2">Note</th>
                        <th className="py-1.5 pr-2 text-right">Montant</th>
                        <th className="py-1.5 pr-2 text-right">Solde après</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.transactions.map((t) => (
                        <tr key={t.id} className="border-b border-[#F0EDE6]" data-testid={`admin-history-tx-${t.id}`}>
                          <td className="py-1.5 pr-2 text-[#5B6B7A]">{(t.created_at || "").replace("T", " ").split(".")[0]}</td>
                          <td className="py-1.5 pr-2">
                            {t.amount > 0 ? (
                              <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><ArrowDownLeft className="w-3 h-3" />{t.type_label}</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-red-700 font-semibold"><ArrowUpRight className="w-3 h-3" />{t.type_label}</span>
                            )}
                          </td>
                          <td className="py-1.5 pr-2">{t.counterparty_name || "—"}</td>
                          <td className="py-1.5 pr-2 text-[#5B6B7A] truncate max-w-[180px]" title={t.note}>{t.note}</td>
                          <td className={`py-1.5 pr-2 text-right font-bold ${t.amount > 0 ? "text-emerald-700" : "text-red-700"}`}>
                            {t.amount > 0 ? "+" : ""}{fmtXAF(t.amount)}
                          </td>
                          <td className="py-1.5 pr-2 text-right font-bold text-[#0F2B46]">{fmtXAF(t.running_balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreatePartnerDialog({ open, onClose, zoneOptions, onCreated }) {
  const [form, setForm] = useState({ partner_name: "", email: "", phone: "", password: "", tft_balance: 0, partner_zones: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setForm({ partner_name: "", email: "", phone: "", password: "", tft_balance: 0, partner_zones: [] }); }, [open]);

  const submit = async () => {
    if (!form.partner_name || !form.email) { toast.error("Nom et email requis"); return; }
    setBusy(true);
    try {
      await api.post("/admin/partners", form);
      toast.success("Partenaire créé");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const toggleZone = (id) => setForm((f) => ({ ...f, partner_zones: f.partner_zones.includes(id) ? f.partner_zones.filter((z) => z !== id) : [...f.partner_zones, id] }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="create-partner-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0F2B46] flex items-center gap-2" style={{ fontFamily: "Sora" }}>
            <Building2 className="w-5 h-5 text-[#F2A93B]" /> Nouveau partenaire / flotte
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#5B6B7A] block mb-1">Nom du partenaire</label>
            <Input value={form.partner_name} onChange={(e) => setForm({ ...form, partner_name: e.target.value })} placeholder="Ex: Trigo Fleet Douala SARL" className="h-9" data-testid="new-partner-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#5B6B7A] block mb-1">Email de connexion</label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-9" data-testid="new-partner-email" />
            </div>
            <div>
              <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#5B6B7A] block mb-1">Téléphone</label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-9" data-testid="new-partner-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#5B6B7A] block mb-1">Mot de passe initial</label>
              <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Partner@123" className="h-9" data-testid="new-partner-password" />
            </div>
            <div>
              <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#5B6B7A] block mb-1">Solde initial TFT</label>
              <Input type="number" value={form.tft_balance} onChange={(e) => setForm({ ...form, tft_balance: parseInt(e.target.value || 0) })} className="h-9" data-testid="new-partner-tft" />
            </div>
          </div>
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#5B6B7A] block mb-2">Zones opérées</label>
            <div className="flex flex-wrap gap-1.5">
              {zoneOptions.map((z) => {
                const on = form.partner_zones.includes(z.id);
                return (
                  <button key={z.id} type="button" onClick={() => toggleZone(z.id)} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${on ? "bg-[#0F2B46] border-[#0F2B46] text-white" : "bg-white border-[#CBD5E1] text-[#0F2B46]"}`} data-testid={`new-partner-zone-${z.id}`}>
                    <MapPin className="w-3 h-3" /> {z.name}
                  </button>
                );
              })}
              {zoneOptions.length === 0 && <span className="text-xs text-[#5B6B7A]">Aucune zone configurée (Express &gt; Zones &amp; tarifs).</span>}
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={onClose} className="border-[#CBD5E1]">Annuler</Button>
            <Button onClick={submit} disabled={busy} className="bg-[#F2A93B] text-[#0F2B46] hover:bg-[#E09520] font-bold" data-testid="new-partner-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Créer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RechargeDialog({ partner, onClose, onRecharged }) {
  const [amount, setAmount] = useState(50000);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (partner) { setAmount(50000); setNote(""); } }, [partner]);

  const submit = async () => {
    if (!partner || amount <= 0) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/partners/${partner.id}/recharge-tft`, { amount, note });
      toast.success(`Solde partenaire : ${fmtXAF(data.tft_balance)}`);
      onRecharged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!partner} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="recharge-partner-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0F2B46] flex items-center gap-2" style={{ fontFamily: "Sora" }}>
            <Wallet className="w-5 h-5 text-[#F2A93B]" /> Recharger : {partner?.partner_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="p-3 rounded-xl bg-[#F4F2ED]">
            <div className="text-[10px] uppercase text-[#5B6B7A]">Solde actuel</div>
            <div className="text-lg font-extrabold text-[#0F2B46]">{fmtXAF(partner?.tft_balance)}</div>
          </div>
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#5B6B7A] block mb-1">Montant à ajouter (F)</label>
            <Input type="number" value={amount} onChange={(e) => setAmount(parseInt(e.target.value || 0))} className="h-9 text-lg font-bold" data-testid="partner-recharge-amount" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {[10000, 25000, 50000, 100000, 250000].map((v) => (
              <Button key={v} type="button" size="sm" variant="outline" onClick={() => setAmount(v)} className="border-[#CBD5E1] h-7 text-xs" data-testid={`partner-recharge-preset-${v}`}>
                +{v.toLocaleString("fr-FR")}
              </Button>
            ))}
          </div>
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#5B6B7A] block mb-1">Note (optionnel)</label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex: Recharge trimestrielle" className="h-9" data-testid="partner-recharge-note" />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={onClose} className="border-[#CBD5E1]">Annuler</Button>
            <Button onClick={submit} disabled={busy} className="bg-[#F2A93B] text-[#0F2B46] hover:bg-[#E09520] font-bold" data-testid="partner-recharge-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Wallet className="w-4 h-4 mr-1" />} Créditer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LinkDialog({ linking, onClose, allDrivers, allVehicles, onChanged }) {
  const [busyId, setBusyId] = useState("");
  if (!linking) return null;
  const isDriver = linking.type === "driver";
  const partner = linking.partner;
  const list = isDriver ? allDrivers : allVehicles;
  const linkedIds = isDriver ? partner.partner_drivers : partner.partner_vehicles;

  const toggle = async (item) => {
    const attached = linkedIds.includes(item.id);
    setBusyId(item.id);
    try {
      await api.post(`/admin/partners/${partner.id}/${isDriver ? "link-driver" : "link-vehicle"}`, {
        [isDriver ? "driver_id" : "vehicle_id"]: item.id,
        attach: !attached,
      });
      toast.success(!attached ? "Ajouté à la flotte" : "Retiré de la flotte");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusyId(""); }
  };

  return (
    <Dialog open={!!linking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="link-partner-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0F2B46] flex items-center gap-2" style={{ fontFamily: "Sora" }}>
            {isDriver ? <UserPlus className="w-5 h-5 text-[#F2A93B]" /> : <Car className="w-5 h-5 text-[#F2A93B]" />}
            {isDriver ? "Chauffeurs" : "Véhicules"} de {partner.partner_name}
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto space-y-1.5">
          {(list || []).length === 0 ? (
            <div className="py-8 text-center text-sm text-[#5B6B7A]">Aucun {isDriver ? "chauffeur" : "véhicule"} disponible.</div>
          ) : list.map((it) => {
            const attached = linkedIds.includes(it.id);
            const title = isDriver ? it.name : `${it.brand} ${it.model}`;
            const subtitle = isDriver ? (it.phone || it.email) : `${it.plate_number || "—"} · ${it.vehicle_type || ""}`;
            return (
              <div key={it.id} className={`p-2.5 rounded-xl border ${attached ? "border-[#F2A93B] bg-[#FEF7EC]" : "border-[#E4E0D8]"} flex items-center gap-2`} data-testid={`link-item-${it.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#0F2B46] text-sm truncate">{title}</div>
                  <div className="text-[11px] text-[#5B6B7A] truncate">{subtitle}</div>
                </div>
                <Button
                  size="sm"
                  variant={attached ? "outline" : "default"}
                  onClick={() => toggle(it)}
                  disabled={busyId === it.id}
                  className={attached ? "border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10" : "bg-[#0F2B46] text-white"}
                  data-testid={`link-toggle-${it.id}`}
                >
                  {busyId === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : attached ? <><Unlink className="w-3 h-3 mr-1" />Retirer</> : <><Link2 className="w-3 h-3 mr-1" />Ajouter</>}
                </Button>
              </div>
            );
          })}
        </div>
        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={onClose} className="border-[#CBD5E1]"><X className="w-4 h-4 mr-1" /> Fermer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PartnerDetailDialog({ partner, onClose, zoneOptions, onSaved }) {
  const [form, setForm] = useState({ partner_name: "", phone: "", partner_zones: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (partner) setForm({ partner_name: partner.partner_name, phone: partner.phone, partner_zones: partner.partner_zones });
  }, [partner]);
  if (!partner) return null;

  const submit = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/partners/${partner.id}`, form);
      toast.success("Partenaire mis à jour");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const toggleZone = (id) => setForm((f) => ({ ...f, partner_zones: f.partner_zones.includes(id) ? f.partner_zones.filter((z) => z !== id) : [...f.partner_zones, id] }));

  return (
    <Dialog open={!!partner} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="partner-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0F2B46]" style={{ fontFamily: "Sora" }}>Détails : {partner.partner_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#5B6B7A] block mb-1">Nom</label>
            <Input value={form.partner_name} onChange={(e) => setForm({ ...form, partner_name: e.target.value })} className="h-9" />
          </div>
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#5B6B7A] block mb-1">Téléphone</label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-9" />
          </div>
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#5B6B7A] block mb-2">Zones opérées</label>
            <div className="flex flex-wrap gap-1.5">
              {zoneOptions.map((z) => {
                const on = form.partner_zones.includes(z.id);
                return (
                  <button key={z.id} type="button" onClick={() => toggleZone(z.id)} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${on ? "bg-[#0F2B46] border-[#0F2B46] text-white" : "bg-white border-[#CBD5E1] text-[#0F2B46]"}`}>
                    <MapPin className="w-3 h-3" /> {z.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={onClose} className="border-[#CBD5E1]">Fermer</Button>
            <Button onClick={submit} disabled={busy} className="bg-[#F2A93B] text-[#0F2B46] hover:bg-[#E09520] font-bold">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Enregistrer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Placeholder to keep imports referenced (Users icon)
const _unused = Users;
